import type { components } from './schema';

/**
 * Record types for the admin screens, taken straight from the generated OpenAPI
 * schema. Nothing here is hand-written: if a DTO changes, these change with it
 * and the screens fail to compile rather than quietly rendering `undefined`.
 */

type S = components['schemas'];

export type AdminInterest = S['AdminInterestDto'];
export type AdminChildEventType = S['AdminChildEventTypeDto'];
export type AdminOptionSet = S['AdminOptionSetDto'];
export type AdminOption = S['AdminOptionDto'];
export type AdminWishlistCategory = S['AdminWishlistCategoryDto'];
export type AdminMarketplace = S['AdminMarketplaceDto'];

export type AdminWizardSummary = S['AdminWizardSummaryDto'];
export type AdminWizardDefinition = S['AdminWizardDefinitionDto'];
export type AdminWizardStep = S['AdminWizardStepDto'];
export type AdminWizardField = S['AdminWizardFieldDto'];
export type WizardStepInput = S['WizardStepInputDto'];
export type WizardFieldInput = S['WizardFieldInputDto'];
export type WizardFieldType = WizardFieldInput['type'];
export type WizardStatus = AdminWizardSummary['status'];

export type AdminUser = S['AdminUserDto'];
export type AdminRoleValue = AdminUser['role'];
export type AdminStatusValue = AdminUser['status'];

export type ClickBucket = S['ClickBucketDto'];
export type TopProduct = S['TopProductDto'];
export type PageMeta = S['PageMetaDto'];

// ---------------------------------------------------------------------------
// Users, and the one audited child read
// ---------------------------------------------------------------------------

export type AdminUserSummary = S['AdminUserSummaryDto'];
export type AdminUserDetail = S['AdminUserDetailDto'];
export type UserAccountStatus = AdminUserSummary['status'];

/**
 * ⚠️ **A child list row: an id, an age in whole years, and timestamps.**
 *
 * There is no `firstName`, no `photoUrl` and no `birthDate` on this type, and
 * that is the contract rather than an omission — `GET /admin/users/:id/children`
 * never selects those columns. A list cannot leak what it was never given
 * (CLAUDE.md §4.1, §4.3), and `AdminChildReveal` is a separate shape from a
 * separate, audited endpoint rather than the same shape with more fields on it.
 */
export type AdminChildSummary = S['AdminChildSummaryDto'];

/** The response of the audited `GET /admin/children/:id`. Never fetched for a list. */
export type AdminChildReveal = S['AdminChildRevealDto'];

// ---------------------------------------------------------------------------
// Moderation
// ---------------------------------------------------------------------------

export type AdminReport = S['AdminReportDto'];
export type ReportStatus = AdminReport['status'];
export type ReportSubjectType = AdminReport['subjectType'];
export type ResolveReportRequest = S['ResolveReportRequestDto'];
export type SearchAnomaly = S['SearchAnomalyDto'];

// ---------------------------------------------------------------------------
// Curated catalogue
// ---------------------------------------------------------------------------

export type AdminCuratedProduct = S['AdminCuratedProductDto'];
export type CuratedProductStatus = AdminCuratedProduct['status'];
export type CuratedProductSource = AdminCuratedProduct['source'];
export type GenderAffinity = AdminCuratedProduct['genderAffinity'];
export type PriceBand = AdminCuratedProduct['priceBand'];
export type CreateCuratedProductRequest = S['CreateCuratedProductRequestDto'];
export type UpdateCuratedProductRequest = S['UpdateCuratedProductRequestDto'];

// ---------------------------------------------------------------------------
// Calendar events
// ---------------------------------------------------------------------------

export type AdminCalendarEvent = S['AdminCalendarEventDto'];
export type CalendarEventType = AdminCalendarEvent['type'];
export type CreateCalendarEventRequest = S['CreateCalendarEventRequestDto'];
export type UpdateCalendarEventRequest = S['UpdateCalendarEventRequestDto'];

// ---------------------------------------------------------------------------
// Ops
// ---------------------------------------------------------------------------

export type AdminNotificationLog = S['AdminNotificationLogDto'];
export type NotificationLogStatus = AdminNotificationLog['status'];
export type NotificationChannel = AdminNotificationLog['channel'];
export type AdminAuditLog = S['AdminAuditLogDto'];

/** Refine resource names. These are the API path segments, verbatim. */
export const RESOURCES = {
  interests: 'admin/interests',
  childEventTypes: 'admin/child-event-types',
  optionSets: 'admin/option-sets',
  options: 'admin/options',
  categories: 'admin/categories',
  marketplaces: 'admin/marketplaces',
  wizards: 'admin/wizards',
  admins: 'admin/admins',
  users: 'admin/users',
  reports: 'admin/reports',
  searchAnomalies: 'admin/search-anomalies',
  curatedProducts: 'admin/curated-products',
  calendarEvents: 'admin/calendar-events',
  notificationLogs: 'admin/notification-logs',
  auditLogs: 'admin/audit-logs',
} as const;

/**
 * The frozen field-type catalogue (01 §5). Adding a type needs a mobile
 * release, so the builder offers exactly these and nothing else.
 */
export const WIZARD_FIELD_TYPES: readonly WizardFieldType[] = [
  'TEXT',
  'TEXTAREA',
  'NUMBER',
  'DATE',
  'SELECT',
  'MULTI_SELECT',
  'RADIO',
  'MULTI_SELECT_ICON',
  'TOGGLE',
];
